import { imobiliarioGeralFormConfig, type VerticalLandingContent } from '@nexofacil/schemas';
import { Instagram, MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import type { GetBrokerResponse, PublicBrokerSummary } from '@/lib/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { NoFilterMatch, PropertyFilterBar, usePropertyFilters } from '@/components/property-filters';
import { loadFormDraft } from '@/lib/form-persistence';
import { propertyStageLabel } from '@/lib/labels';
import { priceLabel, specsLine } from '@/lib/property-display';
import { formatPhoneDisplay } from '@/lib/phone-format';
import type { LandingProps } from './types';

// =============================================================================
// AngelicaLanding · landing da Angélica Coelho, corretora de imóveis
// (pessoa física, CRECI 721172F). Antes era a marca "+Lar"; saiu porque
// soava como imobiliária, que pede outro registro no CRECI. Voz em
// primeira pessoa do singular em toda a página.
//
// A página é sinalização de rodovia: chapa verde com filete branco, letra de
// placa (Barlow), setas grossas, marcos de km. O comprador da Grande Floripa
// lê isso todo dia na BR-101; aqui a placa aponta pro imóvel dele. O dourado
// do logo fica reservado pra próxima ação (o botão do formulário).
// =============================================================================

const C = {
  sign: '#0F5A3A',
  signDeep: '#0B4A30',
  forest: '#03200F',
  night: '#021A0E',
  gold: '#C49547',
  ground: '#F2F4F1',
  asphalt: '#1D2420',
  ink: '#15201A',
  muted: '#4D5B52',
  rule: '#D5DBD6',
  // Amarelo de pintura de pista: separado do dourado, que é só da ação.
  lane: '#F2C12E',
} as const;

const FONT = '"Barlow", system-ui, sans-serif';
const CONDENSED = '"Barlow Condensed", "Barlow", system-ui, sans-serif';
const CRECI = 'CRECI 721172F';
// Recorte do monograma AC do logo enviado pela Angélica (sem a linha de texto).
const MARK_URL = 'https://nexofacil-assets-prod.s3.amazonaws.com/brokers/angelica/mark.jpg';
// Lido do formulário: a placa nunca diverge do número real de etapas.
const FORM_STEPS = imobiliarioGeralFormConfig.steps.length;
const CITIES = ['Florianópolis', 'São José', 'Palhoça', 'Biguaçu'];

const SERVICES: Array<{ title: string; body: string }> = [
  { title: 'Lançamentos', body: 'Imóveis na planta, com tempo pra se planejar até a entrega.' },
  { title: 'Compra e venda', body: 'Da busca à negociação, comigo do seu lado.' },
  { title: 'Financiamento e documentação', body: 'Simulação, aprovação e papelada resolvidas.' },
  { title: 'Assessoria personalizada', body: 'Atendimento no seu ritmo, sem pressão.' },
  { title: 'Investimentos', body: 'Imóveis pra quem quer renda ou valorização.' },
  { title: 'Atendimento especializado', body: 'Atendimento de quem conhece a região de perto.' },
];

// Condições do anúncio da Angélica, confirmadas em 09/10/2026. Placa de
// distâncias: o rótulo à esquerda, o número grande à direita.
const CONDITIONS: Array<{ label: string; value: string }> = [
  { label: 'Seu carro na entrada', value: 'até 100% FIPE' },
  { label: 'Entrada a partir de', value: 'R$ 40 mil' },
  { label: 'Use o seu FGTS', value: 'Aceito' },
];

// FAQ e "Como funciona" próprios, em primeira pessoa. O conteúdo padrão da
// vertical fala no plural (feito pra imobiliária) e não serve aqui.
const FAQ: Array<{ q: string; a: string }> = [
  {
    q: 'Posso dar meu carro como parte da entrada?',
    a: 'Pode. Aceito o seu carro como parte da entrada, pagando até 100% da tabela FIPE.',
  },
  {
    q: 'Qual a entrada mínima?',
    a: 'A entrada começa em R$ 40 mil. Você pode usar o FGTS e o seu carro como parte dela.',
  },
  {
    q: 'Preciso me comprometer com alguma coisa?',
    a: 'Não. Responder o formulário e conversar comigo é sem compromisso. Você decide se quer seguir depois de ver as opções.',
  },
  {
    q: 'Você ajuda com o financiamento?',
    a: 'Sim. Ajudo na simulação e acompanho a aprovação do crédito e a documentação até a assinatura.',
  },
  {
    q: 'Posso usar o FGTS na compra?',
    a: 'Pode, desde que você se enquadre nas regras do FGTS para compra de imóvel residencial. Avalio o seu caso no atendimento.',
  },
  {
    q: 'Trabalha com imóveis na planta e prontos?',
    a: 'Sim. Imóveis prontos e lançamentos, pra morar ou investir.',
  },
  {
    q: 'Tenho um imóvel. Posso dar como parte do pagamento?',
    a: 'Depende do imóvel e da negociação com o vendedor. Converso sobre o seu caso no atendimento.',
  },
  {
    q: 'Como falo com você?',
    a: 'Responda as perguntas rápidas da página. No final, o WhatsApp abre com a sua mensagem pronta, é só enviar.',
  },
];

const HOW_IT_WORKS: Array<{ title: string; body: string }> = [
  { title: 'Conte o que você procura', body: 'Tipo de imóvel, cidade, faixa de valor e prazo. Leva menos de 2 minutos.' },
  { title: 'Receba as opções certas', body: 'Eu separo os imóveis que cabem no seu perfil e no seu planejamento.' },
  { title: 'Feche com segurança', body: 'Acompanho a negociação, o financiamento e a documentação até a entrega das chaves.' },
];

export function AngelicaLanding({ broker, properties, content, onCta }: LandingProps) {
  return (
    <div className="ml min-h-screen" style={{ backgroundColor: C.ground, color: C.ink, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Barlow+Condensed:wght@600;700;800&display=swap"
        />
        <meta name="theme-color" content={C.forest} />
      </Helmet>
      <style>{`
        .ml ::selection { background: ${C.gold}; color: ${C.forest}; }
        .ml :focus-visible { outline: 3px solid ${C.gold}; outline-offset: 3px; box-shadow: 0 0 0 3px ${C.forest}; }
        html { scrollbar-color: ${C.sign} ${C.ground}; }
      `}</style>

      <Gantry broker={broker} onCta={onCta} />
      <Hero broker={broker} content={content} onCta={onCta} />
      <Conditions />
      {properties.length > 0 && <Properties properties={properties} />}
      <Route content={content} />
      <Services />
      <About broker={broker} content={content} />
      <Faq content={content} />
      <FinalExit content={content} onCta={onCta} />
      <Footer broker={broker} content={content} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Primitivas de placa
// ----------------------------------------------------------------------------

/** Chapa verde com o filete branco interno de placa de rodovia. */
function Sign({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-[16px] p-[5px] ${className}`}
      style={{ backgroundColor: C.sign, boxShadow: '0 18px 40px -22px rgba(3, 32, 15, 0.55)' }}
    >
      <div className="h-full rounded-[12px] border-2 border-white/95 text-white">{children}</div>
    </div>
  );
}

/** Seta de placa: haste larga e ponta triangular cheia. */
function RoadArrow({ angle = 0, className = '' }: { angle?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      aria-hidden="true"
      className={className}
      style={{ transform: `rotate(${angle}deg)` }}
    >
      <path d="M24 3 L44 23 H31 V45 H17 V23 H4 Z" fill="currentColor" />
    </svg>
  );
}

function KmMarker({ label }: { label: string }) {
  return (
    <span
      className="inline-flex min-w-[3.25rem] flex-col items-center rounded-[6px] border-2 px-2 py-1 leading-none"
      style={{ borderColor: C.ink, backgroundColor: '#FFFFFF', color: C.ink, fontFamily: CONDENSED }}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider">km</span>
      <span className="text-xl font-bold">{label}</span>
    </span>
  );
}

function CtaButton({
  onClick,
  children,
  className = '',
}: {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group inline-flex h-14 items-center justify-center gap-3 rounded-[12px] px-7 text-lg font-bold transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 active:translate-y-0 ${className}`}
      style={{
        backgroundColor: C.gold,
        color: C.forest,
        fontFamily: CONDENSED,
        letterSpacing: '0.02em',
        boxShadow: '0 10px 24px -12px rgba(196, 149, 71, 0.9)',
      }}
    >
      {children}
      <RoadArrow angle={90} className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
    </button>
  );
}

function SectionHeading({ children, light }: { children: React.ReactNode; light?: boolean }) {
  return (
    <h2
      className="text-balance text-4xl font-bold leading-[1.02] md:text-5xl"
      style={{ fontFamily: CONDENSED, color: light ? '#FFFFFF' : C.forest }}
    >
      {children}
    </h2>
  );
}

// ----------------------------------------------------------------------------
// Pórtico (header)
// ----------------------------------------------------------------------------

function Gantry({ broker, onCta }: { broker: PublicBrokerSummary; onCta: () => void }) {
  return (
    <header className="sticky top-0 z-30" style={{ backgroundColor: C.forest }}>
      <div className="container flex h-16 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <img src={MARK_URL} alt="" className="h-11 w-auto shrink-0 rounded-[6px]" />
          <div className="min-w-0 leading-none">
            <div className="truncate text-xl font-bold text-white" style={{ fontFamily: CONDENSED }}>
              {broker.name}
            </div>
            <div className="mt-1 hidden text-[11px] font-medium uppercase tracking-[0.14em] text-white/70 sm:block">
              Corretora de imóveis
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onCta}
          className="h-11 shrink-0 rounded-[10px] border-2 border-white/90 px-4 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-[#03200F]"
        >
          Encontrar imóvel
        </button>
      </div>
      {/* Faixa da viga do pórtico */}
      <div className="h-1.5" style={{ backgroundColor: C.sign }} />
    </header>
  );
}

// ----------------------------------------------------------------------------
// Hero · a placa principal
// ----------------------------------------------------------------------------

function Hero({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  const subheadline = broker.pageContent.heroSubheadline ?? content.heroSubheadlineDefault;
  const [draftStep, setDraftStep] = useState<number | null>(null);

  // Quem já começou o formulário volta pra etapa onde parou (o próprio
  // formulário restaura o rascunho); aqui só avisamos.
  useEffect(() => {
    const draft = loadFormDraft();
    if (draft && draft.step >= 2 && draft.step <= FORM_STEPS) setDraftStep(draft.step);
  }, []);

  return (
    <section className="pt-6 md:pt-16">
      <div className="container grid gap-7 pb-12 md:grid-cols-[1.6fr_1fr] md:items-center md:gap-14 md:pb-20">
        <Sign>
          <div className="p-5 md:p-12">
            <h1 className="flex items-start justify-between gap-4">
              <span className="block">
                <span
                  className="block text-[3.6rem] font-extrabold leading-[0.9] sm:text-7xl lg:text-[6rem]"
                  style={{ fontFamily: CONDENSED }}
                >
                  Seu imóvel
                </span>
                <span
                  className="mt-2 block text-2xl font-semibold leading-tight text-white/90 md:text-4xl"
                  style={{ fontFamily: CONDENSED }}
                >
                  {FORM_STEPS} etapas · 2 minutos · <span className="whitespace-nowrap">sem compromisso</span>
                </span>
              </span>
              <RoadArrow angle={45} className="mt-1 h-14 w-14 shrink-0 md:h-28 md:w-28" />
            </h1>

            <div className="my-5 h-0.5 bg-white/85 md:my-9" />

            <ul
              className="grid grid-cols-2 gap-x-3 gap-y-1 text-lg font-semibold sm:flex sm:flex-wrap md:text-2xl"
              style={{ fontFamily: CONDENSED }}
              aria-label="Cidades atendidas"
            >
              {CITIES.map((city, i) => (
                <li key={city} className="flex items-center gap-3">
                  {i > 0 && <span aria-hidden="true" className="hidden h-1.5 w-1.5 rounded-full bg-white/70 sm:block" />}
                  {city}
                </li>
              ))}
            </ul>
          </div>
        </Sign>

        <div className="flex flex-col">
          {/* No celular o botão vem colado na placa; o parágrafo fica depois. */}
          <p
            className="order-2 mt-6 max-w-md text-lg leading-relaxed md:order-1 md:mt-0 md:text-xl"
            style={{ color: C.muted }}
          >
            {subheadline}
          </p>
          <div className="order-1 md:order-2 md:mt-7">
            <CtaButton onClick={onCta} className="w-full sm:w-auto">
              {content.heroPrimaryCta}
            </CtaButton>
            <p className="mt-3 text-sm font-medium" style={{ color: C.muted }}>
              Você recebe o retorno pelo WhatsApp.
            </p>
          </div>

          {draftStep && (
            <button
              type="button"
              onClick={onCta}
              className="order-3 mt-6 flex w-full items-center gap-4 rounded-[12px] border-2 bg-white p-3 text-left transition-colors hover:bg-[#F7F8F6] sm:w-auto"
              style={{ borderColor: C.ink }}
            >
              <KmMarker label={String(draftStep)} />
              <span className="leading-snug">
                <span className="block text-sm font-semibold" style={{ color: C.ink }}>
                  Você parou na etapa {draftStep} de {FORM_STEPS}
                </span>
                <span className="block text-sm underline underline-offset-4" style={{ color: C.sign }}>
                  Continuar de onde parei
                </span>
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Asfalto com a faixa central tracejada: o hero termina na estrada. */}
      <div aria-hidden="true" className="relative h-12 md:h-16" style={{ backgroundColor: C.asphalt }}>
        <div
          className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2"
          style={{
            backgroundImage: `linear-gradient(90deg, ${C.lane} 55%, transparent 0)`,
            backgroundSize: '64px 100%',
          }}
        />
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Condições · placa de distâncias com as ofertas do anúncio
// ----------------------------------------------------------------------------

function Conditions() {
  return (
    <section className="py-14 md:py-20">
      <div className="container grid gap-8 md:grid-cols-[1fr_1.4fr] md:items-center md:gap-16">
        <SectionHeading>Condições que cabem no seu bolso</SectionHeading>
        <Sign>
          <dl className="divide-y divide-white/30">
            {CONDITIONS.map(({ label, value }) => (
              <div
                key={label}
                className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 md:px-8 md:py-5"
              >
                <dt className="text-lg font-semibold leading-tight md:text-xl" style={{ fontFamily: CONDENSED }}>
                  {label}
                </dt>
                <dd
                  className="shrink-0 text-3xl font-extrabold leading-none sm:text-right md:text-4xl"
                  style={{ fontFamily: CONDENSED }}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Sign>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Rota · como funciona, com o trecho percorrido desenhado na rolagem
// ----------------------------------------------------------------------------

function useReached<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [reached, setReached] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setReached(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setReached(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -35% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, reached };
}

function Route({ content }: { content: VerticalLandingContent }) {
  return (
    <section id="como-funciona" className="py-16 md:py-24" style={{ backgroundColor: '#FFFFFF' }}>
      <div className="container grid gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
        <div>
          <SectionHeading>Como funciona</SectionHeading>
          {/* Sem número: a placa do hero já diz quantas etapas o formulário tem. */}
          <p className="mt-4 max-w-sm text-lg leading-relaxed" style={{ color: C.muted }}>
            Do formulário à entrega das chaves.
          </p>
        </div>
        <ol className="relative">
          {HOW_IT_WORKS.map((step, i) => (
            <RouteStep
              key={step.title}
              km={String(i + 1)}
              title={step.title}
              body={step.body}
              last={i === HOW_IT_WORKS.length - 1}
            />
          ))}
        </ol>
      </div>
    </section>
  );
}

function RouteStep({ km, title, body, last }: { km: string; title: string; body: string; last: boolean }) {
  const { ref, reached } = useReached<HTMLLIElement>();
  return (
    <li ref={ref} className="relative grid grid-cols-[3.25rem_1fr] items-start gap-5 pb-10 last:pb-0">
      {!last && (
        <span aria-hidden="true" className="absolute bottom-0 left-[1.625rem] top-14 w-1 -translate-x-1/2">
          {/* Tracejado do trecho que falta; a linha cheia cobre conforme o visitante chega */}
          <span
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(${C.rule} 60%, transparent 0)`,
              backgroundSize: '100% 14px',
            }}
          />
          <span
            className="absolute inset-x-0 top-0 origin-top transition-transform duration-700 ease-out motion-reduce:transition-none"
            style={{
              bottom: 0,
              backgroundColor: C.sign,
              transform: reached ? 'scaleY(1)' : 'scaleY(0)',
            }}
          />
        </span>
      )}
      <KmMarker label={km} />
      <div className="pt-1">
        <h3 className="text-2xl font-bold leading-tight" style={{ fontFamily: CONDENSED, color: C.forest }}>
          {title}
        </h3>
        <p className="mt-1.5 max-w-md text-base leading-relaxed" style={{ color: C.muted }}>
          {body}
        </p>
      </div>
    </li>
  );
}

// ----------------------------------------------------------------------------
// Serviços · placa de serviços, uma linha por serviço
// ----------------------------------------------------------------------------

function Services() {
  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <SectionHeading>O que eu faço por você</SectionHeading>
        <Sign className="mt-8 md:mt-10">
          <ul className="grid md:grid-cols-2">
            {SERVICES.map(({ title, body }, i) => (
              <li
                key={title}
                className={`flex items-center gap-4 border-white/30 p-4 md:p-6 ${
                  i > 0 ? 'border-t' : ''
                } ${i === 1 ? 'md:border-t-0' : ''} ${i % 2 === 1 ? 'md:border-l' : ''}`}
              >
                {/* Marcador, não seta: a linha não é link. */}
                <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-white" />
                <span>
                  <span className="block text-xl font-bold leading-tight" style={{ fontFamily: CONDENSED }}>
                    {title}
                  </span>
                  <span className="mt-0.5 block text-sm leading-snug text-white/80">{body}</span>
                </span>
              </li>
            ))}
          </ul>
        </Sign>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Imóveis · aparece só quando a Angélica publicar pelo painel
// ----------------------------------------------------------------------------

function Properties({ properties }: { properties: GetBrokerResponse['properties'] }) {
  const { filtered, filters } = usePropertyFilters(properties);
  return (
    <section id="imoveis" className="pb-16 md:pb-24">
      <div className="container">
        <SectionHeading>Imóveis prontos e lançamentos</SectionHeading>
        <PropertyFilterBar filters={filters} accent={C.sign} className="mt-8" />
        {filtered.length === 0 ? (
          <NoFilterMatch filters={filters} />
        ) : (
          <div
            className={`mt-8 grid gap-6 md:grid-cols-2 ${filtered.length >= 3 ? 'lg:grid-cols-3' : ''}`}
          >
            {filtered.map((p) => (
              <PropertySign key={p.id} property={p} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Card de imóvel: a foto em cima e, embaixo, a placa com nome, local e preço. */
function PropertySign({ property }: { property: GetBrokerResponse['properties'][number] }) {
  const cover = property.photos[0];
  const place = [property.neighborhood, property.city].filter(Boolean).join(', ');
  return (
    <Link
      to={`/empreendimento/${property.slug}`}
      className="group flex flex-col overflow-hidden rounded-[16px] bg-white transition-transform duration-200 hover:-translate-y-1"
      style={{ boxShadow: '0 18px 40px -24px rgba(3, 32, 15, 0.55)' }}
    >
      <div className="relative aspect-[4/3] overflow-hidden" style={{ backgroundColor: C.rule }}>
        {cover && (
          <img
            src={cover}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        )}
        {property.stage && (
          <span
            className="absolute left-3 top-3 rounded-[6px] border-2 bg-white px-2.5 py-1 text-sm font-bold uppercase tracking-wide"
            style={{ borderColor: C.ink, color: C.ink, fontFamily: CONDENSED }}
          >
            {propertyStageLabel[property.stage]}
          </span>
        )}
        {property.featured && (
          <span
            className="absolute right-3 top-3 rounded-[6px] border-2 border-white px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-white"
            style={{ backgroundColor: C.sign, fontFamily: CONDENSED }}
          >
            Destaque
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-[5px]" style={{ backgroundColor: C.sign }}>
        <div className="flex flex-1 items-end justify-between gap-3 rounded-[10px] border-2 border-white/95 p-4 text-white">
          <div className="min-w-0">
            <h3 className="line-clamp-2 text-2xl font-bold leading-tight" title={property.name} style={{ fontFamily: CONDENSED }}>
              {property.name}
            </h3>
            <p className="mt-0.5 truncate text-sm text-white/80">{place}</p>
            {property.kind === 'listing' && specsLine(property.specs) && (
              <p className="mt-1 text-sm text-white/90">{specsLine(property.specs)}</p>
            )}
            <p className="mt-2 text-lg font-semibold" style={{ fontFamily: CONDENSED }}>
              {priceLabel(property)}
            </p>
          </div>
          <RoadArrow
            angle={90}
            className="h-8 w-8 shrink-0 transition-transform duration-200 group-hover:translate-x-1"
          />
        </div>
      </div>
    </Link>
  );
}

// ----------------------------------------------------------------------------
// Quem atende · placa branca com o logo
// ----------------------------------------------------------------------------

function About({ broker, content }: { broker: PublicBrokerSummary; content: VerticalLandingContent }) {
  const about = broker.pageContent.aboutText ?? broker.bio;
  if (!about) return null;

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: '#FFFFFF' }}>
      <div className="container">
        <div
          className="grid gap-8 rounded-[16px] border-2 p-6 md:grid-cols-[220px_1fr] md:items-center md:gap-12 md:p-10"
          style={{ borderColor: C.ink }}
        >
          {broker.avatarUrl && (
            <img
              src={broker.avatarUrl}
              alt={broker.name}
              className="aspect-square w-40 rounded-[12px] object-cover md:w-full"
              loading="lazy"
            />
          )}
          <div>
            <SectionHeading>{content.aboutTitle}</SectionHeading>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.12em]" style={{ color: C.sign }}>
              {broker.name} · {CRECI}
            </p>
            <div className="mt-5 max-w-[65ch] space-y-4 text-base leading-relaxed" style={{ color: C.muted }}>
              {about
                .split('\n')
                .filter(Boolean)
                .map((p) => (
                  <p key={p}>{p}</p>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Perguntas frequentes
// ----------------------------------------------------------------------------

function Faq({ content }: { content: VerticalLandingContent }) {
  return (
    <section className="py-16 md:py-24">
      <div className="container grid gap-10 md:grid-cols-[1fr_1.6fr] md:gap-16">
        <div>
          <SectionHeading>{content.faqTitle}</SectionHeading>
          <p className="mt-4 text-lg" style={{ color: C.muted }}>
            {content.faqSubtitle}
          </p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((item) => (
            <AccordionItem key={item.q} value={item.q} style={{ borderColor: C.rule }}>
              <AccordionTrigger className="text-left text-lg font-semibold" style={{ color: C.forest }}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed" style={{ color: C.muted }}>
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Saída final · pórtico verde com a última placa
// ----------------------------------------------------------------------------

function FinalExit({ content, onCta }: { content: VerticalLandingContent; onCta: () => void }) {
  return (
    <section style={{ backgroundColor: C.forest }}>
      <div className="h-1.5" style={{ backgroundColor: C.sign }} />
      <div className="container py-16 md:py-24">
        <Sign className="mx-auto max-w-3xl">
          <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <h2 style={{ fontFamily: CONDENSED }}>
                <span className="block text-5xl font-extrabold leading-[0.95] md:text-6xl">Seu imóvel</span>
                <span className="mt-1 block text-2xl font-semibold text-white/90">na próxima saída</span>
              </h2>
              <p className="mt-3 max-w-sm text-white/85">{content.finalCtaSubtitle}</p>
            </div>
            <RoadArrow angle={90} className="hidden h-20 w-20 shrink-0 md:block" />
          </div>
        </Sign>
        <div className="mt-8 text-center">
          <CtaButton onClick={onCta} className="w-full sm:w-auto">
            {content.finalCtaButton}
          </CtaButton>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Rodapé
// ----------------------------------------------------------------------------

function Footer({ broker, content }: { broker: PublicBrokerSummary; content: VerticalLandingContent }) {
  const instagram = broker.pageContent.socialLinks?.instagram;
  const handle = instagram?.replace(/\/+$/, '').split('/').pop();
  const year = new Date().getFullYear();

  return (
    <footer className="text-white" style={{ backgroundColor: C.night }}>
      <div className="container grid gap-10 py-12 md:grid-cols-2">
        <div className="flex items-start gap-4">
          {broker.avatarUrl && (
            <img src={broker.avatarUrl} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" loading="lazy" />
          )}
          <div>
            <div className="text-xl font-bold" style={{ fontFamily: CONDENSED }}>
              {broker.name}
            </div>
            {broker.bio && <p className="mt-2 max-w-md text-sm leading-relaxed text-white/70">{broker.bio}</p>}
          </div>
        </div>
        <div className="space-y-3 text-sm md:justify-self-end">
          {/* Número como texto, sem link direto: o contato passa pelo formulário
              (lead registrado e evento pra Meta). */}
          <span className="flex items-center gap-2 py-2 text-white/90">
            <MessageCircle className="h-4 w-4 text-white/70" />
            {formatPhoneDisplay(broker.whatsappNumber)}
          </span>
          {instagram && (
            <a
              href={instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/90 transition-colors hover:text-white"
            >
              <Instagram className="h-4 w-4 text-white/70" />@{handle}
            </a>
          )}
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container flex flex-col items-center justify-between gap-3 py-5 text-xs text-white/55 sm:flex-row">
          <span>
            © {year} {broker.name} · {CRECI} · {content.footerLabel}
          </span>
          <a href="/privacidade" className="inline-block py-3 underline underline-offset-4 transition-colors hover:text-white/80">
            Política de privacidade
          </a>
        </div>
      </div>
    </footer>
  );
}
